import {skipToken} from '@reduxjs/toolkit/query';
import {DataStatus, OptionType, Segment, Separator} from 'platform/components';
import {Box, Show, VStack} from 'platform/foundation';

import {useState} from 'react';

import {isNotNilOrEmpty} from 'ramda-adjunct';

import {useDeleteCommentMutation, useGetCommentsQuery} from '@dms/api/core';
import {EntityResourceIds} from '@dms/api/shared';
import {GetServiceCaseOrdersApiResponse} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {CommentCardItem, handleApiError, useGetUserOptions} from '@dms/shared';

interface ServiceCaseCommentsTabProps {
  serviceOrders: GetServiceCaseOrdersApiResponse;
}

export function ServiceCaseCommentsTab(props: ServiceCaseCommentsTabProps) {
  const [selectedServiceOrderId, setSelectedServiceOrderId] = useState<string>();
  const [deletingCommentId, setDeletingCommentId] = useState<string>();
  const [deleteComment] = useDeleteCommentMutation();
  const {userOptions} = useGetUserOptions();

  const activeServiceOrderId = props.serviceOrders?.some(
    (order) => order.serviceOrderId === selectedServiceOrderId
  )
    ? (selectedServiceOrderId ?? '')
    : (props.serviceOrders?.[0]?.serviceOrderId ?? '');

  const {
    data: comments,
    isLoading: isCommentsLoading,
    isError,
  } = useGetCommentsQuery(
    isNotNilOrEmpty(activeServiceOrderId)
      ? {
          resourceId: EntityResourceIds.serviceOrder,
          recordId: activeServiceOrderId,
        }
      : skipToken
  );

  const options: OptionType<string, string>[] = (props.serviceOrders ?? []).map((order) => ({
    value: order.serviceOrderId,
    label: order.number,
  }));

  const handleCommentDelete = (commentId: string) => {
    setDeletingCommentId(commentId);
    void deleteComment({
      resourceId: EntityResourceIds.serviceOrder,
      recordId: activeServiceOrderId,
      commentId,
    })
      .unwrap()
      .catch(handleApiError)
      .finally(() => setDeletingCommentId(undefined));
  };

  return (
    <VStack spacing={4} grow={1} minHeight={0}>
      <Show when={isNotNilOrEmpty(options)}>
        <Segment
          value={activeServiceOrderId}
          onChange={setSelectedServiceOrderId}
          options={options}
        />
      </Show>

      <Box flex={1} overflow="auto" minHeight={0}>
        <DataStatus
          minHeight={35}
          isLoading={isCommentsLoading}
          isError={isError}
          isEmpty={!comments?.length}
          emptyMessage={i18n.t('page.comments.labels.noOneCommented')}
        >
          {comments?.map((comment, index) => (
            <VStack key={comment.id}>
              <CommentCardItem
                comment={comment}
                userOptions={userOptions}
                isDeleteCommentLoading={deletingCommentId === comment.id}
                onDelete={handleCommentDelete}
              />

              <Show when={index !== comments.length - 1}>
                <Separator spacing={0} />
              </Show>
            </VStack>
          ))}
        </DataStatus>
      </Box>
    </VStack>
  );
}
